import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { LaboursService } from '../../services/labours.service';
import { finalize, Subscription } from 'rxjs';
import { Action, PaginatedResponse, TableColumn } from '../../../common/models/common.model';
import { Labour, LabourAdvance } from '../../production-inventory.model';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { RawMaterialWarehouse } from '../../../raw-material-inventory/raw-material.model';
import { UserRole } from '../../../employees/employees.model';
import { SnackbarService } from '../../../services/snackbar.service';
import { SnackbarAlertStatus } from '../../../utils';

@Component({
  selector: 'app-labours',
  standalone: false,
  templateUrl: './labours.component.html',
  styleUrl: './labours.component.scss',
})
export class LaboursComponent implements OnInit, OnDestroy {
  @ViewChild('labourFormModalContent') labourFormModalContent!: TemplateRef<any>;
  @ViewChild('salaryRangeDateContent') salaryRangeDateContent!: TemplateRef<any>;
  @ViewChild('advanceModalContent') advanceModalContent!: TemplateRef<any>;

  @Input() warehouseControl: FormControl = new FormControl();
  @Input() wareHouses: RawMaterialWarehouse[] = [];

  protected selectedWarehouseId: string | null = null;
  protected searchTerm: string = '';
  protected filterWarehouseId: string = '';
  protected isLaboursDataLoading: boolean = false;
  protected laboursList: Labour[] = [];
  protected currentUser: any = null;

  labourForm!: FormGroup;
  selectedFile: File | null = null;
  previewUrl: string | null = null;
  protected labourData: Labour | null = null;
  protected modalRef!: NgbModalRef;
  protected salaryRangeForm!: FormGroup;
  protected advanceForm!: FormGroup;
  protected selectedLabourForAdvance: Labour | null = null;
  protected advancesList: LabourAdvance[] = [];
  protected isAdvancesLoading: boolean = false;
  protected selectedLabourForDownload: Labour | null = null;

  private subscriptions: Subscription = new Subscription();
  private laboursSubscription!: Subscription;
  private searchDebounceTimer!: any;
  protected currentPage: number = 1;

  protected readonly laboursTableColumn: TableColumn<Labour>[] = [
    { label: 'Full Name', field: 'full_name' },
    { label: 'Mobile Number', field: 'mobile_number' },
    { label: 'Gender', field: 'gender' },
    { label: 'Type of Labour', field: 'type_of_labour' },
    { label: 'Department', field: 'department' },
    { label: 'Warehouse', field: 'warehouse.name' },
  ];

  protected hasNextPage = false;
  protected hasPreviousPage = false;
  protected totalCount: number = 0;

  protected readonly Action = Action;
  protected readonly UserRole = UserRole;

  protected readonly genderOptions = [
    { label: 'Male', value: 'Male' },
    { label: 'Female', value: 'Female' },
    { label: 'Other', value: 'Other' },
  ];

  protected readonly labourTypeOptions = [
    { label: 'Skilled', value: 'Skilled' },
    { label: 'Unskilled', value: 'Unskilled' },
    { label: 'Supervisor', value: 'Supervisor' },
  ];

  protected readonly departmentOptions = [
    { label: 'Mixing', value: 'Mixing' },
    { label: 'Packing', value: 'Packing' },
    { label: 'Loading', value: 'Loading' },
    { label: 'Other', value: 'Other' },
  ];

  constructor(
    private laboursService: LaboursService,
    private fb: FormBuilder,
    private modalService: NgbModal,
    private snackbarService: SnackbarService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.selectedWarehouseId = this.warehouseControl?.value || '';
    this.initializeLabourForm();

    this.salaryRangeForm = this.fb.group({
      from_date: ['', Validators.required],
      to_date: ['', Validators.required],
    });

    this.advanceForm = this.fb.group({
      amount: ['', [Validators.required]],
      date: ['', Validators.required],
      note: [''],
    });

    this.warehouseControl.valueChanges.subscribe((warehouseId: string) => {
      this.selectedWarehouseId = warehouseId;
      this.currentPage = 1;
      this.getLaboursData();
    });

    this.getLaboursData();
  }

  getSalaryDetails(): void {
    if (this.salaryRangeForm.valid) {
      const { from_date, to_date } = this.salaryRangeForm.value;
      const q: Record<string, any> = { from_date, to_date };

      if (this.selectedLabourForDownload) {
        q['labour_id'] = this.selectedLabourForDownload.id;
      }

      this.laboursSubscription = this.laboursService.download_salary_sheet(q).subscribe({
        next: ({ file_url }) => {
          const a = document.createElement('a');
          a.href = file_url;
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          a.remove();
          this.selectedLabourForDownload = null;
          this.modalRef?.dismiss();
        },
        error: err => {
          const message = err?.error?.detail ?? 'No attendance records found for the given date range.';
          this.snackbarService.show(message, SnackbarAlertStatus.ERROR);
          this.selectedLabourForDownload = null;
          this.modalRef?.dismiss();
        },
      });
    }
  }

  protected viewAdvanceLedger(labour: Labour): void {
    void this.router.navigate(['/production-inventory/labour', labour.id, 'advance-ledger'], {
      state: { name: labour.full_name },
    });
  }

  protected openSingleLabourSalaryModal(labour: Labour): void {
    this.selectedLabourForDownload = labour;
    this.salaryRangeForm.reset();
    this.modalRef = this.modalService.open(this.salaryRangeDateContent, { size: 'md', backdrop: 'static' });
  }

  protected openForm(labour: Labour | null = null) {
    this.labourForm.reset();
    this.previewUrl = null;
    this.selectedFile = null;
    this.labourData = labour;

    if (labour) {
      this.labourForm.setValue({
        warehouse_id: labour.warehouse?.id ?? '',
        full_name: labour.full_name ?? '',
        aadhar_number: labour.aadhar_number ?? '',
        date_of_birth: labour.date_of_birth ?? '',
        gender: labour.gender ?? '',
        father_or_spouse_name: labour.father_or_spouse_name ?? '',
        mobile_number: labour.mobile_number ?? '',
        alternate_number: labour.alternate_number ?? '',
        address: labour.address ?? '',
        type_of_labour: labour.type_of_labour ?? '',
        department: labour.department ?? '',
        date_of_joining: labour.date_of_joining ?? '',
        daily_wages: labour.daily_wages ?? '',
        working_hours: labour.working_hours ?? '',
        per_hour_rate: labour.per_hour_rate ?? '',
        ot_rate: labour.ot_rate ?? '',
        ta_per_day: labour.ta_per_day ?? '',
      });

      this.previewUrl = labour.photo_id ?? null;
    }

    this.modalRef = this.modalService.open(this.labourFormModalContent, { size: 'lg', backdrop: 'static' });
  }

  protected onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.selectedFile = input.files[0];
      const reader = new FileReader();
      reader.onload = () => (this.previewUrl = reader.result as string);
      reader.readAsDataURL(this.selectedFile);
    }
  }

  protected submitForm() {
    if (this.labourForm.invalid) return;

    const formData = new FormData();
    Object.entries(this.labourForm.value).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, String(value));
      } else {
        formData.append(key, '');
      }
    });

    if (this.selectedFile) {
      formData.append('photo_id', this.selectedFile);
    }

    if (this.labourData) {
      this.laboursSubscription = this.laboursService.update(this.labourData.id, formData).subscribe({
        next: (updatedLaboursData: Labour) => {
          const labourTobeUpdate = this.laboursList.findIndex((labour: Labour) => labour.id === this.labourData?.id);
          this.laboursList[labourTobeUpdate] = updatedLaboursData;
          this.modalRef?.close();
        },
      });
    } else {
      this.laboursSubscription = this.laboursService.post(formData).subscribe({
        next: (newLabour: Labour) => {
          this.laboursList.push(newLabour);
          this.modalRef?.close();

          this.currentPage = 1;
          this.getLaboursData();
        },
      });
    }
  }

  protected openSalaryRangeModal(content: TemplateRef<any>): void {
    this.selectedLabourForDownload = null;
    this.salaryRangeForm.reset();
    this.modalRef = this.modalService.open(content, { size: 'md', backdrop: 'static' });
  }

  protected onDeleteLabour(labour: Labour): void {
    this.laboursSubscription = this.laboursService.delete(labour.id).subscribe({
      next: () => {
        this.laboursList = this.laboursList.filter(lbr => lbr.id !== labour.id);

        this.currentPage = 1;
        this.getLaboursData();
      },
    });
  }

  protected onSearchKeyUp(): void {
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this.getLaboursData();
    }, 500);
  }

  protected onWarehouseFilterChange(): void {
    this.currentPage = 1;
    this.getLaboursData();
  }

  private getLaboursData(): void {
    this.isLaboursDataLoading = true;

    const q: Record<string, any> = {};

    q['page'] = this.currentPage;

    const warehouseId = this.filterWarehouseId || this.selectedWarehouseId;
    if (warehouseId) {
      q['warehouse'] = warehouseId;
    }

    if (this.searchTerm) {
      q['full_name'] = this.searchTerm;
    }

    this.laboursSubscription = this.laboursService
      .list(q)
      .pipe(finalize(() => (this.isLaboursDataLoading = false)))
      .subscribe({
        next: (laboursResponse: PaginatedResponse<Labour>) => {
          this.laboursList = laboursResponse.results ?? [];
          this.totalCount = laboursResponse.count;
          this.hasNextPage = !!laboursResponse.next;
          this.hasPreviousPage = !!laboursResponse.previous;
        },
      });
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.getLaboursData();
  }

  protected openAdvanceModal(content: TemplateRef<any>, labour: Labour): void {
    this.selectedLabourForAdvance = labour;
    this.advanceForm.reset({ date: new Date().toISOString().split('T')[0], note: '' });
    this.advancesList = [];
    this.isAdvancesLoading = true;

    this.laboursService.get_advances(labour.id).subscribe({
      next: (advances: LabourAdvance[]) => {
        this.advancesList = advances;
        this.isAdvancesLoading = false;
      },
      error: () => (this.isAdvancesLoading = false),
    });

    this.modalRef = this.modalService.open(content, { size: 'lg', backdrop: 'static' });
  }

  protected submitAdvance(): void {
    if (this.advanceForm.invalid || !this.selectedLabourForAdvance) return;

    this.laboursService.add_advance(this.selectedLabourForAdvance.id, this.advanceForm.value).subscribe({
      next: (newAdvance: LabourAdvance) => {
        this.advancesList.unshift(newAdvance);
        this.advanceForm.reset({ date: new Date().toISOString().split('T')[0], note: '' });
      },
    });
  }

  private initializeLabourForm(): void {
    this.labourForm = this.fb.group({
      warehouse_id: ['', Validators.required],
      full_name: ['', Validators.required],
      aadhar_number: ['', Validators.required],
      date_of_birth: ['', Validators.required],
      gender: ['', Validators.required],
      father_or_spouse_name: [''],
      mobile_number: ['', Validators.required],
      alternate_number: [''],
      address: ['', Validators.required],
      type_of_labour: ['', Validators.required],
      department: ['', Validators.required],
      date_of_joining: ['', Validators.required],
      daily_wages: [''],
      working_hours: [''],
      per_hour_rate: [''],
      ot_rate: [''],
      ta_per_day: [''],
    });
  }

  ngOnDestroy(): void {
    this.laboursSubscription?.unsubscribe();
    this.subscriptions.unsubscribe();
    this.modalRef?.dismiss();
  }
}
